// src/pages/CalendarPage/components/ApiStatusIndicator.tsx

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../../../store/authStore';

const DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true';

const Indicator = styled(motion.div)<{ $status: 'ok' | 'error' | 'unknown' }>`
  position: fixed;
  bottom: 100px;
  right: 16px;
  padding: 8px 12px;
  border-radius: 20px;
  background: ${props => {
    switch (props.$status) {
      case 'ok': return 'rgba(34, 197, 94, 0.9)';
      case 'error': return 'rgba(239, 68, 68, 0.9)';
      default: return 'rgba(148, 163, 184, 0.9)';
    }
  }};
  color: white;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  cursor: pointer;
`;

const StatusDot = styled.div<{ $status: 'ok' | 'error' | 'unknown' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
  animation: ${props => (props.$status === 'ok' ? 'pulse 2s infinite' : 'none')};

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const ExpandedInfo = styled(motion.div)`
  position: fixed;
  bottom: 140px;
  right: 16px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 12px;
  padding: 16px;
  color: white;
  font-size: 11px;
  z-index: 9999;
  min-width: 200px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
`;

const InfoValue = styled.span`
  font-weight: 600;
`;

export const ApiStatusIndicator: React.FC = () => {
  const { apiStatus, user } = useAuthStore();
  const[expanded, setExpanded] = useState(false);
  const[lastCheck, setLastCheck] = useState<Date | null>(null);

  // Хуки вызываются всегда. DEV-логика — внутри эффекта.
  useEffect(() => {
    if (!DEV_MODE) return;

    let mounted = true;

    const check = async () => {
      await useAuthStore.getState().checkApiStatus();
      if (mounted) setLastCheck(new Date());
    };

    check();
    const interval = setInterval(check, 30000); // каждые 30 сек

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  },[]);

  // Ранний выход допускается только после всех хуков
  if (!DEV_MODE) return null;

  return (
    <>
      <Indicator
        $status={apiStatus}
        onClick={() => setExpanded(prev => !prev)}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <StatusDot $status={apiStatus} />
        {apiStatus === 'ok' ? 'API OK' : apiStatus === 'error' ? 'API ERROR' : 'API ?'}
      </Indicator>

      <AnimatePresence>
        {expanded && (
          <ExpandedInfo
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            <InfoRow>
              <InfoLabel>API:</InfoLabel>
              <InfoValue>{(process.env.REACT_APP_API_BASE || '').slice(0, 40)}...</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Status:</InfoLabel>
              <InfoValue>{apiStatus.toUpperCase()}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>User ID:</InfoLabel>
              <InfoValue>{user?.telegram_id ?? 'N/A'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Dev Mode:</InfoLabel>
              <InfoValue>{DEV_MODE ? 'ON' : 'OFF'}</InfoValue>
            </InfoRow>
            {lastCheck && (
              <InfoRow>
                <InfoLabel>Last check:</InfoLabel>
                <InfoValue>{lastCheck.toLocaleTimeString()}</InfoValue>
              </InfoRow>
            )}
          </ExpandedInfo>
        )}
      </AnimatePresence>
    </>
  );
};
